/**
 * 主线程文件 Hash Worker 客户端。
 *
 * 统一管理请求 Promise、ArrayBuffer 所有权转移与取消；调用方不直接持有 Worker。
 */
import type {
	HashWorkerRequest,
	HashWorkerResponse,
} from "./hash-worker-protocol";

const HASH_FILE_CHUNK_SIZE = 1024 * 1024;

type HashResult = { kind: "hash"; sha256: string };
type ReceiveResult = { kind: "receive"; sha256: string; blob: Blob };
type WorkerResult = HashResult | ReceiveResult;

type PendingTask = {
	resolve: (result: WorkerResult) => void;
	reject: (error: Error) => void;
};

type FileHashWorker = {
	postMessage: (request: HashWorkerRequest, transfer?: Transferable[]) => void;
	terminate: () => void;
	onmessage: ((event: MessageEvent<HashWorkerResponse>) => void) | null;
	onerror: ((event: ErrorEvent) => void) | null;
};

const createWorker = (): FileHashWorker =>
	new Worker(new URL("./hash.worker.ts", import.meta.url), {
		type: "module",
	});

class FileHashWorkerClient {
	private worker: FileHashWorker | null = null;
	private readonly pending = new Map<string, PendingTask>();

	/** 在 Worker 中按分片计算完整文件 SHA-256。 */
	hashFile(taskId: string, file: Blob): Promise<string> {
		return this.createPending(taskId, {
			type: "hash-file",
			taskId,
			file,
			chunkSize: HASH_FILE_CHUNK_SIZE,
		}).then((result) => {
			if (result.kind !== "hash") throw new Error("Unexpected worker result");
			return result.sha256;
		});
	}

	/** 初始化接收任务；后续 chunk 的 ArrayBuffer 会转移到 Worker。 */
	startReceive(taskId: string, mime: string): void {
		this.ensureWorker().postMessage({ type: "receive-start", taskId, mime });
	}

	/** 把一个接收分片的所有权转移给 Worker。 */
	appendReceiveChunk(taskId: string, chunk: ArrayBuffer): void {
		this.ensureWorker().postMessage({ type: "receive-chunk", taskId, chunk }, [
			chunk,
		]);
	}

	/** 完成接收 Hash，并取回由 Worker 直接组装的 Blob。 */
	finalizeReceive(taskId: string): Promise<{ sha256: string; blob: Blob }> {
		return this.createPending(taskId, {
			type: "receive-finalize",
			taskId,
		}).then((result) => {
			if (result.kind !== "receive")
				throw new Error("Unexpected worker result");
			return { sha256: result.sha256, blob: result.blob };
		});
	}

	/** 幂等取消任务并释放 Worker 内引用。 */
	cancel(taskId: string): void {
		if (this.worker) this.worker.postMessage({ type: "cancel", taskId });
		const pending = this.pending.get(taskId);
		if (pending) {
			this.pending.delete(taskId);
			pending.reject(new Error("File hash task cancelled"));
		}
	}

	private createPending(
		taskId: string,
		request: HashWorkerRequest,
	): Promise<WorkerResult> {
		if (this.pending.has(taskId)) {
			return Promise.reject(new Error("Duplicate file hash task"));
		}
		return new Promise((resolve, reject) => {
			this.pending.set(taskId, { resolve, reject });
			try {
				this.ensureWorker().postMessage(request);
			} catch (error) {
				this.pending.delete(taskId);
				reject(
					error instanceof Error ? error : new Error("Worker unavailable"),
				);
			}
		});
	}

	private ensureWorker(): FileHashWorker {
		if (this.worker) return this.worker;
		const worker = createWorker();
		worker.onmessage = (event) => this.handleResponse(event.data);
		worker.onerror = (event) => {
			this.failAll(new Error(event.message || "File hash worker crashed"));
		};
		this.worker = worker;
		return worker;
	}

	private handleResponse(response: HashWorkerResponse): void {
		const pending = this.pending.get(response.taskId);
		if (!pending) return;
		this.pending.delete(response.taskId);
		if (response.type === "error") {
			pending.reject(new Error(response.message));
			return;
		}
		if (response.type === "hash-complete") {
			pending.resolve({ kind: "hash", sha256: response.sha256 });
			return;
		}
		pending.resolve({
			kind: "receive",
			sha256: response.sha256,
			blob: response.blob,
		});
	}

	private failAll(error: Error): void {
		for (const pending of this.pending.values()) pending.reject(error);
		this.pending.clear();
		this.worker?.terminate();
		this.worker = null;
	}
}

/** 文件传输会话共享的 Worker 客户端。 */
export const fileHashWorker = new FileHashWorkerClient();
